import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Section, Field, Money, RozetListesi, grid } from './DetayParcalari';
import { alimTuruLabel, fmtTarih, butceTertibiText, firmaTeklifleri, enDusukTeklif, parseTL, toTLString } from './detayOrtak';
import { tesisTuruText } from '../../../utils/tesisTuru';

function PdfIcon() {
  return (
    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  );
}

function FirmaTeklifTablosu({ record: r, displayMoney }) {
  const teklifler = firmaTeklifleri(r);
  const enDusuk = enDusukTeklif(teklifler);
  const isiYapan = (r.isi_yapan_firma || '').trim().toLocaleLowerCase('tr');
  if (!teklifler.length) return <p className="text-sm text-gray-500">Firma teklifi girilmemiş.</p>;
  return (
    <div className="overflow-x-auto rounded-lg border border-gray-800">
      <table className="w-full text-sm" data-testid="dt-detail-teklif-tablosu">
        <thead className="bg-gray-800 text-gray-400 text-xs uppercase">
          <tr><th className="px-3 py-2 text-left">#</th><th className="px-3 py-2 text-left">Firma</th><th className="px-3 py-2 text-right">Teklif</th><th className="px-3 py-2 text-left">Not</th></tr>
        </thead>
        <tbody>
          {teklifler.map(f => {
            const secildi = isiYapan && f.adi.trim().toLocaleLowerCase('tr') === isiYapan;
            return (
              <tr key={f.no} className={`border-t border-gray-800 ${secildi ? 'bg-emerald-900/15' : ''}`} data-testid={`dt-detail-firma-${f.no}`}>
                <td className="px-3 py-2 text-gray-400">{f.no}</td>
                <td className="px-3 py-2 text-white">{f.adi || '-'}</td>
                <td className="px-3 py-2 text-right text-white font-medium">{displayMoney(f.teklif) || '-'}</td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-1">
                    {enDusuk?.no === f.no && <Badge className="bg-blue-500/20 text-blue-300 border-blue-500/50 text-[10px]">En düşük teklif</Badge>}
                    {secildi && <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/50 text-[10px]">İşi yapan firma</Badge>}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// Doğrudan Temin işi bilgi kartı (İhaleler detay penceresiyle aynı düzen)
export function DTDetailDialog({ open, onOpenChange, record: r, displayMoney, onEdit, onExportPDF, canEdit = true }) {
  if (!r) return null;
  const odenek = parseTL(r.odenek_tutari);
  const sozlesme = parseTL(r.sozlesme_bedeli);
  const odenen = parseTL(r.odenen_tutar);
  const fark = odenek && sozlesme ? odenek - sozlesme : null;
  const kalan = sozlesme ? sozlesme - odenen : null;
  const tertipler = butceTertibiText(r);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="dt-detail-dialog">
        <DialogHeader>
          <DialogTitle className="text-blue-400 flex flex-wrap items-center gap-2" data-testid="dt-detail-title">
            Doğrudan Temin Detayları{r.yil ? `: ${r.yil}` : ''}
            {r.odenek_id && <Badge className="bg-emerald-900/40 text-emerald-300 border-emerald-700/50 text-[11px]" data-testid="dt-detail-odenek-badge">Ödenekten aktarıldı</Badge>}
          </DialogTitle>
          <DialogDescription className="text-gray-400" data-testid="dt-detail-isin-adi">{r.isin_adi || '-'}</DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <Section no="1" title="Temel Bilgiler" testId="dt-detail-section-1">
            <div className={grid}>
              <Field label="Yıl" value={r.yil} testId="dt-detail-yil" />
              <Field label="Tesis Türü" value={tesisTuruText(r.tesis_turu)} testId="dt-detail-tesis-turu" />
              <Field label="Tesis Adı" value={r.tesis_adi} testId="dt-detail-tesis-adi" />
              <Field label="İlçe" value={r.ilce} testId="dt-detail-ilce" />
              <Field label="Alım Türü" value={alimTuruLabel(r.alim_turu)} testId="dt-detail-alim-turu" />
              <Field label="Talep Yeri" value={r.talep_yeri} testId="dt-detail-talep-yeri" />
              <Field label="İşin Adı" value={r.isin_adi} className="col-span-2 md:col-span-3" testId="dt-detail-isin-adi-alan" />
            </div>
          </Section>

          <Section no="2" title="Sorumlular ve Piyasa Araştırması" testId="dt-detail-section-2">
            <div className={grid}>
              <Field label="İlgilenen Memur" value={r.ilgilenen_memur} testId="dt-detail-memur" />
              <div className="col-span-2" data-testid="dt-detail-piyasa-gorevlileri">
                <span className="text-gray-500">Piyasa Araştırma Görevlileri:</span>
                <RozetListesi items={r.piyasa_arastirma_gorevlileri || []} />
              </div>
              <div className="col-span-2 md:col-span-3" data-testid="dt-detail-butce-tertibi">
                <span className="text-gray-500">Bütçe Tertibi:</span>
                <RozetListesi items={tertipler} />
              </div>
            </div>
          </Section>

          <Section no="3" title="Firma Teklifleri" testId="dt-detail-section-3">
            <div className="space-y-3">
              <FirmaTeklifTablosu record={r} displayMoney={displayMoney} />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <Money label="İşi Yapan Firma" value={r.isi_yapan_firma} testId="dt-detail-isi-yapan-firma" tone="bg-emerald-900/20 border border-emerald-700/40" />
                <Money label="Sözleşme Bedeli (KDV Hariç)" value={displayMoney(r.sozlesme_bedeli)} testId="dt-detail-sozlesme-bedeli" tone="bg-emerald-900/20 border border-emerald-700/40" />
              </div>
            </div>
          </Section>

          <Section no="4" title="Sözleşme ve Ödeme" testId="dt-detail-section-4">
            <div className="space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                <Money label="Ödenek Tutarı" value={displayMoney(r.odenek_tutari)} testId="dt-detail-odenek-tutari" />
                <Money label="Sözleşme Bedeli" value={displayMoney(r.sozlesme_bedeli)} testId="dt-detail-sozlesme-bedeli-2" />
                <Money label="İşin Süresi" value={r.isin_suresi ? `${r.isin_suresi} gün` : ''} testId="dt-detail-isin-suresi" />
                <Money label="Ödeme Tarihi" value={fmtTarih(r.odeme_tarihi)} testId="dt-detail-odeme-tarihi" />
                <Money label="Ödeme Belge No" value={r.odeme_belge_no} testId="dt-detail-odeme-belge-no" />
                <Money label="Ödenen Tutar" value={displayMoney(r.odenen_tutar)} testId="dt-detail-odenen-tutar" />
              </div>
              <div className="bg-blue-900/20 border border-blue-500 rounded-lg p-3">
                <h4 className="text-blue-400 font-medium text-sm mb-2">Özet</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <Money label="Ödenek − Sözleşme Farkı" value={fark === null ? '' : `${fark < 0 ? '-' : ''}${displayMoney(toTLString(Math.abs(fark)))}`} testId="dt-detail-fark" tone={fark !== null && fark < 0 ? 'bg-red-900/30' : 'bg-blue-900/30'} />
                  <Money label="Kalan Ödeme (Sözleşme − Ödenen)" value={kalan === null ? '' : displayMoney(toTLString(Math.max(kalan, 0)))} testId="dt-detail-kalan" tone={kalan !== null && kalan <= 0 ? 'bg-emerald-900/30' : 'bg-blue-900/30'} />
                </div>
              </div>
              {(r.last_modified_by_name || r.last_modified_at) && (
                <p className="text-xs text-gray-500" data-testid="dt-detail-son-duzenleyen">
                  Son düzenleyen: {r.last_modified_by_name || '-'}{r.last_modified_at ? ` · ${new Date(r.last_modified_at).toLocaleString('tr-TR')}` : ''}
                </p>
              )}
            </div>
          </Section>
        </div>

        <DialogFooter>
          {canEdit && (
            <Button onClick={() => { onOpenChange(false); onEdit(r); }} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="dt-detail-edit-btn">Düzenle</Button>
          )}
          <Button onClick={() => onExportPDF(r)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="dt-detail-pdf-btn"><PdfIcon />PDF İndir</Button>
          <Button onClick={() => onOpenChange(false)} className="bg-gray-800 text-white hover:bg-gray-700" data-testid="dt-detail-close-btn">Kapat</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
